import React from 'react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { LawEnforcementProfile } from '../../types';

interface LawEnforcementProfileViewProps {
  profile: LawEnforcementProfile;
  onEdit?: () => void;
  showEditButton?: boolean;
}

export const LawEnforcementProfileView: React.FC<LawEnforcementProfileViewProps> = ({
  profile,
  onEdit,
  showEditButton = true
}) => {
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'not_applied': { variant: 'default' as const, text: 'Not Applied', color: 'gray' },
      'pending': { variant: 'warning' as const, text: 'Pending Review', color: 'yellow' },
      'approved': { variant: 'success' as const, text: 'Approved', color: 'green' },
      'rejected': { variant: 'error' as const, text: 'Rejected', color: 'red' },
      'auto_approved': { variant: 'success' as const, text: 'Auto Approved', color: 'green' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.not_applied;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCompletionPercentage = () => {
    let completed = 0;
    let total = 5; // Total required fields

    if (profile.governmentEmailVerified) completed++;
    if (profile.departmentName) completed++;
    if (profile.rankPosition) completed++;
    if (profile.officeAddress) completed++;
    if (profile.governmentIdFile) completed++;

    return Math.round((completed / total) * 100);
  };

  const completionPercentage = getCompletionPercentage();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Law Enforcement Profile
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Your verification status and profile information
          </p>
        </div>
        {showEditButton && onEdit && (
          <Button onClick={onEdit} variant="primary">
            Edit Profile
          </Button>
        )}
      </div>

      {/* Status Overview */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Verification Status
          </h3>
          {getStatusBadge(profile.verificationStatus)}
        </div>
        
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300 mb-2">
            <span>Profile Completion</span>
            <span>{completionPercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>

        {profile.verificationStatus === 'rejected' && profile.rejectionReason && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
              Rejection Reason
            </h4>
            <p className="text-sm text-red-700 dark:text-red-300">
              {profile.rejectionReason}
            </p>
          </div>
        )}

        {profile.adminNotes && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
              Admin Notes
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {profile.adminNotes}
            </p>
          </div>
        )}
      </Card>

      {/* Profile Information Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Verification */}
        <Card className="p-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center mr-3">
              <span className="text-indigo-600 dark:text-indigo-400 text-lg">📧</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Email Verification
            </h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Status</span>
              <div className="flex items-center">
                {profile.governmentEmailVerified ? (
                  <div className="flex items-center text-green-600 dark:text-green-400">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium">Verified</span>
                  </div>
                ) : (
                  <div className="flex items-center text-gray-500 dark:text-gray-400">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-sm font-medium">Not Verified</span>
                  </div>
                )}
              </div>
            </div>
            
            {profile.governmentEmail && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">Email</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {profile.governmentEmail}
                </span>
              </div>
            )}
          </div>
        </Card>

        {/* Office Details */}
        <Card className="p-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3">
              <span className="text-blue-600 dark:text-blue-400 text-lg">🏢</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Office Details
            </h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Department</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {profile.departmentName || 'Not provided'}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Rank/Position</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {profile.rankPosition || 'Not provided'}
              </span>
            </div>
            
            <div className="flex items-start justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Office Address</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white text-right max-w-xs">
                {profile.officeAddress || 'Not provided'}
              </span>
            </div>
          </div>
        </Card>

        {/* Contact Information */}
        <Card className="p-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mr-3">
              <span className="text-green-600 dark:text-green-400 text-lg">📱</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Contact Information
            </h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">WhatsApp</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {profile.whatsappNumber || 'Not provided'}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Telegram</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {profile.telegramNumber || 'Not provided'}
              </span>
            </div>
          </div>
        </Card>

        {/* Document Upload */}
        <Card className="p-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mr-3">
              <span className="text-purple-600 dark:text-purple-400 text-lg">📄</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Government ID
            </h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Status</span>
              <div className="flex items-center">
                {profile.governmentIdFile ? (
                  <div className="flex items-center text-green-600 dark:text-green-400">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium">Uploaded</span>
                  </div>
                ) : (
                  <div className="flex items-center text-gray-500 dark:text-gray-400">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-sm font-medium">Not Uploaded</span>
                  </div>
                )}
              </div>
            </div>
            
            {profile.governmentIdFile && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      <div className="font-medium text-gray-900 dark:text-white">Document uploaded and ready for review</div>
                      <div className="text-xs break-all">
                        {(() => {
                          try {
                            const url = String(profile.governmentIdFile);
                            const filename = url.split('/').filter(Boolean).slice(-1)[0] || 'document';
                            return `File: ${filename}`;
                          } catch {
                            return 'File available';
                          }
                        })()}
                      </div>
                    </div>
                  </div>
                  <a
                    href={String(profile.governmentIdFile)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    View
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </a>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Profile Timeline */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Profile Timeline
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-900 dark:text-white">Profile Created</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {formatDate(profile.createdAt)}
              </p>
            </div>
          </div>

          {profile.updatedAt !== profile.createdAt && (
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Last Updated</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {formatDate(profile.updatedAt)}
                </p>
              </div>
            </div>
          )}

          {profile.reviewedAt && (
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Reviewed</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {formatDate(profile.reviewedAt)}
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default LawEnforcementProfileView;
